/**
 * VendorsTable — unified table listing ALL vendors (one row per vendor).
 *
 * Columns: Name · Payment terms (chips) · Signals · Spend · Score · Stage
 *
 * Features:
 *  - Sortable column headers (client-side)
 *  - Filters: search, term-chip click-to-filter, spend range, stage, label
 *  - Multi-select checkboxes for campaign creation AND label assignment
 *  - Clickable rows → VendorDrillDown inside a Dialog (full vendor detail modal)
 *  - Campaign dropdown (filter to campaign members)
 *  - Campaign membership badges on rows + click-to-filter
 *  - "Create negotiation campaign" button → <CampaignDialog>
 *  - Gmail-style vendor label chips + unified label control (filter ↔ assign)
 *  - Easy "Exclude" row action (hover → ExcludeVendorDialog) + N excluded · view affordance
 */

import React, { useState, useMemo, useRef, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Building2,
  Search,
  MessageSquarePlus,
  DollarSign,
  Clock,
  AlertTriangle,
  Tag,
  TrendingDown,
  Timer,
  AlertCircle,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Ban,
  RotateCcw,
  X,
  Mail,
  Zap,
  Plus,
  Pencil,
  Trash2,
  Check,
  ChevronDown,
  type LucideIcon,
} from "lucide-react";
import { formatCurrency } from "@/lib/currency";
import type { RosterEntry, RosterStage, SpendWindow } from "@/lib/par/vendorRoster";
import type { SignalKey } from "@/lib/par/negotiationSuggestions";
import type { NegotiationCampaign, NegotiationThread } from "@/types/negotiations";
import { useVendorContacts } from "@/hooks/useVendorContacts";
import { useVendorLabels, type VendorLabel } from "@/hooks/useVendorLabels";
import { useToast } from "@/hooks/use-toast";
import { CampaignDialog } from "@/components/negotiations/CampaignDialog";
import { ExcludeVendorDialog } from "@/components/negotiations/ExcludeVendorDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { VendorDrillDown } from "@/components/par/VendorDrillDown";
import { TablePagination } from "@/components/par/TablePagination";
import type { PARInvoiceRow } from "@/lib/par/csvInvoiceParser";

// ---------------------------------------------------------------------------
// Signal icon + tooltip metadata (moved from SuggestedVendorsPanel)
// ---------------------------------------------------------------------------

interface SignalMeta {
  Icon: LucideIcon;
  colorClass: string;
  tooltip: string;
}

const SIGNAL_META: Record<SignalKey, SignalMeta> = {
  high_spend: {
    Icon: DollarSign,
    colorClass: "text-warning",
    tooltip: "High spend (top quartile) — meaningful discount opportunity.",
  },
  no_discount: {
    Icon: Tag,
    colorClass: "text-destructive",
    tooltip: "No early-pay discount terms available — first-time negotiation opportunity.",
  },
  paid_late: {
    Icon: AlertTriangle,
    colorClass: "text-destructive",
    tooltip: "Paid late or sporadically — establishing structured terms could reduce friction.",
  },
  mixed_terms: {
    Icon: TrendingDown,
    colorClass: "text-muted-foreground",
    tooltip: "Mixed payment terms across invoices — standardising could unlock discounts.",
  },
  paid_early_no_capture: {
    Icon: Clock,
    colorClass: "text-primary",
    tooltip: "Already paying early without capturing a discount — formalise to save.",
  },
  long_terms: {
    Icon: Timer,
    colorClass: "text-muted-foreground",
    tooltip: "Long net terms (≥45 days) — sizeable early-pay window creates negotiating leverage.",
  },
};

// ---------------------------------------------------------------------------
// Stage badge styling
// ---------------------------------------------------------------------------

const STAGE_BADGE: Record<RosterStage, string> = {
  Engaged: "bg-primary/10 text-primary border-primary/30",
  Ready: "bg-success/10 text-success border-success/30",
  Qualified: "bg-warning/10 text-warning border-warning/30",
  Identify: "bg-muted text-muted-foreground border-border/60",
};

// ---------------------------------------------------------------------------
// Sort
// ---------------------------------------------------------------------------

type SortKey = "vendor_name" | "payment_terms" | "total_invoiced" | "score" | "stage";

type SortDir = "asc" | "desc";

function sortRoster(roster: RosterEntry[], key: SortKey, dir: SortDir): RosterEntry[] {
  return [...roster].sort((a, b) => {
    let diff = 0;
    switch (key) {
      case "vendor_name":
        diff = a.vendor_name.localeCompare(b.vendor_name);
        break;
      case "payment_terms":
        diff = a.payment_terms.localeCompare(b.payment_terms);
        break;
      case "total_invoiced":
        diff = a.total_invoiced - b.total_invoiced;
        break;
      case "score":
        diff = a.score - b.score;
        break;
      case "stage": {
        const stageOrder: Record<RosterStage, number> = {
          Engaged: 0,
          Ready: 1,
          Qualified: 2,
          Identify: 3,
        };
        diff = stageOrder[a.stage] - stageOrder[b.stage];
        break;
      }
    }
    return dir === "asc" ? diff : -diff;
  });
}

// ---------------------------------------------------------------------------
// SortableHeader
// ---------------------------------------------------------------------------

function SortableHeader({
  label,
  sortKey,
  currentKey,
  currentDir,
  onSort,
  className = "",
}: {
  label: string;
  sortKey: SortKey;
  currentKey: SortKey | null;
  currentDir: SortDir;
  onSort: (key: SortKey) => void;
  className?: string;
}) {
  const isActive = currentKey === sortKey;
  return (
    <TableHead
      className={`text-xs font-semibold cursor-pointer select-none hover:bg-muted/40 transition-colors ${className}`}
      onClick={() => onSort(sortKey)}
      aria-sort={isActive ? (currentDir === "asc" ? "ascending" : "descending") : "none"}
    >
      <div className="flex items-center gap-1">
        {label}
        {isActive ? (
          currentDir === "asc" ? (
            <ArrowUp className="w-3 h-3 text-primary flex-shrink-0" />
          ) : (
            <ArrowDown className="w-3 h-3 text-primary flex-shrink-0" />
          )
        ) : (
          <ArrowUpDown className="w-3 h-3 text-muted-foreground/50 flex-shrink-0" />
        )}
      </div>
    </TableHead>
  );
}

// ---------------------------------------------------------------------------
// Label color helpers
// ---------------------------------------------------------------------------

/** Convert a hex/named color to a light background tint for chips. */
function labelStyle(color: string | null): React.CSSProperties {
  if (!color) return {};
  return { backgroundColor: `${color}22`, borderColor: `${color}66`, color };
}

// ---------------------------------------------------------------------------
// LabelChip — single colored chip rendered on a vendor row
// ---------------------------------------------------------------------------

function LabelChip({
  label,
  onRemove,
  onClick,
}: {
  label: VendorLabel;
  onRemove?: (e: React.MouseEvent) => void;
  onClick?: (e: React.MouseEvent) => void;
}) {
  const style = labelStyle(label.color);
  return (
    <span
      role="button"
      tabIndex={onClick ? 0 : -1}
      onClick={onClick}
      onKeyDown={
        onClick ? (e) => e.key === "Enter" && onClick(e as unknown as React.MouseEvent) : undefined
      }
      aria-label={`Label: ${label.name}`}
      className="inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0 rounded border leading-4 cursor-pointer transition-opacity hover:opacity-80"
      style={
        label.color
          ? style
          : {
              backgroundColor: "hsl(var(--primary) / 0.1)",
              borderColor: "hsl(var(--primary) / 0.3)",
              color: "hsl(var(--primary))",
            }
      }
    >
      {label.name}
      {onRemove && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove(e);
          }}
          aria-label={`Remove label ${label.name}`}
          className="ml-0.5 hover:opacity-70 transition-opacity"
        >
          <X className="w-2 h-2" />
        </button>
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------
// UnifiedLabelControl — one control that switches between filter and setter mode.
//
// NO vendors selected → FILTER mode: checking a label narrows the table to
//   vendors with that label (multi-select AND filter, same behaviour as the old
//   label-filter Select). "Create new label" creates only (no assignment).
//
// ≥1 vendor selected → SETTER mode: checking a label assigns it to all selected
//   vendors, unchecking unassigns (Gmail-style, indeterminate when mixed). "Create
//   new label" creates AND assigns to the selection.
//
// The same popover also hosts rename / delete affordances for label management.
// ---------------------------------------------------------------------------

interface UnifiedLabelControlProps {
  labels: VendorLabel[];
  selectedVendors: Set<string>;
  labelFilter: string | null;
  labelsByVendor: Record<string, VendorLabel[]>;
  onAssign: (vendorName: string, labelId: string) => Promise<unknown>;
  onUnassign: (vendorName: string, labelId: string) => Promise<unknown>;
  onCreateLabel: (name: string) => Promise<unknown>;
  onRenameLabel: (id: string, name: string) => Promise<unknown>;
  onDeleteLabel: (id: string) => Promise<unknown>;
  onLabelFilterChange: (labelId: string | null) => void;
}

function UnifiedLabelControl({
  labels,
  selectedVendors,
  labelFilter,
  labelsByVendor,
  onAssign,
  onUnassign,
  onCreateLabel,
  onRenameLabel,
  onDeleteLabel,
  onLabelFilterChange,
}: UnifiedLabelControlProps) {
  const isSetterMode = selectedVendors.size > 0;
  const [open, setOpen] = useState(false);
  const [newLabelName, setNewLabelName] = useState("");
  const [creatingNew, setCreatingNew] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const menuRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
        setCreatingNew(false);
        setEditingId(null);
      }
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  /** For a given label, count how many selected vendors have it assigned (setter mode). */
  function assignedCount(labelId: string): number {
    let count = 0;
    for (const vn of selectedVendors) {
      if ((labelsByVendor[vn] ?? []).some((l) => l.id === labelId)) count++;
    }
    return count;
  }

  // ── Setter mode: toggle assign/unassign Gmail-style ──

  async function handleSetterToggle(label: VendorLabel) {
    const total = selectedVendors.size;
    const assigned = assignedCount(label.id);
    const vendorList = Array.from(selectedVendors);
    if (assigned === total) {
      // All selected have it → unassign all
      await Promise.all(vendorList.map((vn) => onUnassign(vn, label.id)));
      toast({ title: `Removed label "${label.name}" from ${total} vendor${total > 1 ? "s" : ""}` });
    } else {
      // Assign only to vendors that don't have it yet
      const toAssign = vendorList.filter(
        (vn) => !(labelsByVendor[vn] ?? []).some((l) => l.id === label.id)
      );
      await Promise.all(toAssign.map((vn) => onAssign(vn, label.id)));
      toast({
        title: `Applied label "${label.name}" to ${toAssign.length} vendor${toAssign.length > 1 ? "s" : ""}`,
      });
    }
  }

  // ── Filter mode: toggle the label filter ──

  function handleFilterToggle(labelId: string) {
    onLabelFilterChange(labelFilter === labelId ? null : labelId);
  }

  // ── Create new label ──

  async function handleCreateLabel() {
    const name = newLabelName.trim();
    if (!name) return;
    const newLabel = (await onCreateLabel(name)) as VendorLabel | undefined;
    // In setter mode, also assign the newly created label to all selected vendors
    if (isSetterMode && newLabel?.id) {
      const vendorList = Array.from(selectedVendors);
      await Promise.all(vendorList.map((vn) => onAssign(vn, newLabel.id)));
      toast({
        title: `Created and applied "${name}" to ${vendorList.length} vendor${vendorList.length > 1 ? "s" : ""}`,
      });
    }
    setNewLabelName("");
    setCreatingNew(false);
  }

  // ── Rename ──

  async function handleRename(id: string) {
    const name = editName.trim();
    if (!name) return;
    await onRenameLabel(id, name);
    setEditingId(null);
    setEditName("");
  }

  // ── Determine if trigger should appear active (filter mode has active filter) ──

  const hasActiveFilter = !isSetterMode && labelFilter !== null;

  // ── Trigger label ──

  const triggerLabel = isSetterMode
    ? `Label ${selectedVendors.size} vendor${selectedVendors.size > 1 ? "s" : ""}`
    : "Filter by label";

  return (
    <div className="relative" ref={menuRef}>
      <Button
        size="sm"
        variant={hasActiveFilter ? "default" : "outline"}
        className="h-8 text-xs gap-1.5"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={isSetterMode ? "Label selected vendors" : "Filter by label"}
      >
        <Tag className="w-3.5 h-3.5" />
        {triggerLabel}
        <ChevronDown className="w-3 h-3 opacity-60" />
      </Button>

      {open && (
        <div
          className="absolute left-0 top-full mt-1 z-50 bg-popover border border-border rounded-md shadow-lg w-60 py-1"
          role="listbox"
          aria-label={isSetterMode ? "Label menu" : "Label filter"}
        >
          {/* Mode header */}
          <p className="text-[10px] font-semibold text-muted-foreground px-3 py-1.5 uppercase tracking-wide border-b border-border/50">
            {isSetterMode
              ? `Label ${selectedVendors.size} vendor${selectedVendors.size > 1 ? "s" : ""}`
              : "Filter by label"}
          </p>

          {labels.length === 0 && !creatingNew && (
            <p className="text-xs text-muted-foreground px-3 py-2">No labels yet</p>
          )}

          {/* "All labels" option in filter mode */}
          {!isSetterMode && labels.length > 0 && (
            <button
              onClick={() => {
                onLabelFilterChange(null);
                setOpen(false);
              }}
              className={`flex items-center gap-2 w-full px-3 py-1.5 hover:bg-muted/50 transition-colors text-xs ${
                labelFilter === null ? "text-foreground font-medium" : "text-muted-foreground"
              }`}
              aria-label="Show all labels"
            >
              <span
                className={`w-3.5 h-3.5 flex-shrink-0 rounded border flex items-center justify-center ${
                  labelFilter === null ? "bg-primary border-primary" : "border-border"
                }`}
              >
                {labelFilter === null && <Check className="w-2.5 h-2.5 text-primary-foreground" />}
              </span>
              All labels
            </button>
          )}

          {labels.map((label) => {
            if (editingId === label.id) {
              return (
                <div key={label.id} className="flex items-center gap-1 px-2 py-1">
                  <Input
                    autoFocus
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleRename(label.id);
                      if (e.key === "Escape") {
                        setEditingId(null);
                        setEditName("");
                      }
                    }}
                    className="h-6 text-xs flex-1"
                  />
                  <button
                    onClick={() => handleRename(label.id)}
                    aria-label={`Save rename for ${label.name}`}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Check className="w-3.5 h-3.5" />
                  </button>
                </div>
              );
            }

            // Determine checked/partial state
            let isChecked = false;
            let isPartial = false;
            if (isSetterMode) {
              const count = assignedCount(label.id);
              const total = selectedVendors.size;
              isChecked = count === total;
              isPartial = count > 0 && count < total;
            } else {
              isChecked = labelFilter === label.id;
            }

            return (
              <div
                key={label.id}
                className="flex items-center gap-2 px-3 py-1.5 hover:bg-muted/50 transition-colors group"
              >
                {/* Checkbox toggle */}
                <button
                  role="option"
                  aria-selected={isChecked}
                  aria-label={`Toggle label ${label.name}`}
                  onClick={() =>
                    isSetterMode ? handleSetterToggle(label) : handleFilterToggle(label.id)
                  }
                  className="flex items-center gap-2 flex-1 min-w-0 text-left"
                >
                  <span
                    className={`w-3.5 h-3.5 flex-shrink-0 rounded border transition-colors flex items-center justify-center ${
                      isChecked
                        ? "bg-primary border-primary"
                        : isPartial
                          ? "bg-primary/40 border-primary/60"
                          : "border-border"
                    }`}
                  >
                    {isChecked && <Check className="w-2.5 h-2.5 text-primary-foreground" />}
                    {isPartial && <span className="w-1.5 h-0.5 bg-primary-foreground rounded" />}
                  </span>
                  <LabelChip label={label} />
                </button>

                {/* Edit / delete affordances */}
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingId(label.id);
                      setEditName(label.name);
                    }}
                    aria-label={`Rename label ${label.name}`}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Pencil className="w-3 h-3" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteLabel(label.id);
                    }}
                    aria-label={`Delete label ${label.name}`}
                    className="text-muted-foreground hover:text-destructive transition-colors"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            );
          })}

          {/* Create new label */}
          <div className="border-t border-border/50 mt-1 pt-1">
            {creatingNew ? (
              <div className="flex items-center gap-1 px-2 py-1">
                <Input
                  autoFocus
                  placeholder="New label name…"
                  value={newLabelName}
                  onChange={(e) => setNewLabelName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleCreateLabel();
                    if (e.key === "Escape") {
                      setCreatingNew(false);
                      setNewLabelName("");
                    }
                  }}
                  className="h-6 text-xs flex-1"
                />
                <button
                  onClick={handleCreateLabel}
                  aria-label="Confirm new label"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Check className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setCreatingNew(true)}
                className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                aria-label="Create new label"
              >
                <Plus className="w-3.5 h-3.5" />
                {isSetterMode ? "Create and apply new label" : "Create new label"}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface VendorsTableProps {
  roster: RosterEntry[];
  /** All vendor names — passed to CampaignDialog for the add-from-scratch picker. */
  allVendorNames: string[];
  /** Called when a vendor row is clicked — kept for backwards-compat but no longer drives navigation. */
  onVendorClick?: (vendorName: string) => void;
  /** Active campaigns for campaign-filter dropdown. */
  campaigns: NegotiationCampaign[];
  /** All negotiation threads — used to filter by campaign membership. */
  threads: NegotiationThread[];
  /** Currently selected spend time window (controls Spend column & sort). */
  spendWindow?: SpendWindow;
  /** Called when the operator changes the spend window selector. */
  onSpendWindowChange?: (window: SpendWindow) => void;
  /** Optional slot rendered right-aligned in the top toolbar (e.g. SignalConfigDialog gear). */
  headerActions?: React.ReactNode;
  /** Full PAR invoice rows — used to power VendorDrillDown in the row-click modal. */
  allInvoiceRows?: PARInvoiceRow[];
}

// ---------------------------------------------------------------------------
// Filters state
// ---------------------------------------------------------------------------

const ALL_STAGES: RosterStage[] = ["Identify", "Qualified", "Ready", "Engaged"];
const PAGE_SIZE = 50;

/** Special sentinel — filter to excluded vendors regardless of stage. */
type StageFilterValue = RosterStage | "all" | "excluded";

const SPEND_WINDOW_LABELS: Record<SpendWindow, string> = {
  "3m": "Last 3 months",
  "6m": "Last 6 months",
  "12m": "Last 12 months",
  all: "All time",
};

export function VendorsTable({
  roster,
  allVendorNames,
  onVendorClick,
  campaigns,
  threads,
  spendWindow: spendWindowProp,
  onSpendWindowChange,
  headerActions,
  allInvoiceRows = [],
}: VendorsTableProps) {
  const { setExcluded } = useVendorContacts();
  const {
    labels,
    labelsByVendor,
    assignLabel,
    unassignLabel,
    createLabel,
    renameLabel,
    deleteLabel,
  } = useVendorLabels();

  // ── Sort state ──
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  // ── Filter state ──
  const [search, setSearch] = useState("");
  const [selectedTerms, setSelectedTerms] = useState<string[]>([]);
  const [selectedSignals, setSelectedSignals] = useState<SignalKey[]>([]);
  const [minSpend, setMinSpend] = useState("");
  const [maxSpend, setMaxSpend] = useState("");
  const [stageFilter, setStageFilter] = useState<StageFilterValue>("all");
  // Label filter — null means "all"
  const [labelFilter, setLabelFilter] = useState<string | null>(null);

  // Reset label filter if the pointed-at label no longer exists (e.g. it was deleted)
  useEffect(() => {
    if (labelFilter !== null && !labels.some((l) => l.id === labelFilter)) {
      setLabelFilter(null);
    }
  }, [labels, labelFilter]);

  // ── Spend window (internal state, overridden by prop when provided) ──
  const [internalSpendWindow, setInternalSpendWindow] = useState<SpendWindow>("12m");
  const spendWindow = spendWindowProp ?? internalSpendWindow;

  function handleSpendWindowChange(w: SpendWindow) {
    setInternalSpendWindow(w);
    onSpendWindowChange?.(w);
  }

  // ── Campaign filter ──
  const [campaignFilter, setCampaignFilter] = useState<string>("all");

  // ── Multi-select ──
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [campaignOpen, setCampaignOpen] = useState(false);

  // ── Modal state ──
  const [modalVendor, setModalVendor] = useState<RosterEntry | null>(null);
  const [excludeTarget, setExcludeTarget] = useState<string | null>(null);

  // ── Pagination ──
  const [page, setPage] = useState(0);

  // Count of excluded vendors (across the whole roster, not just filtered)
  const excludedCount = useMemo(() => roster.filter((e) => e.is_excluded).length, [roster]);

  // Build a set of vendor names belonging to the selected campaign
  const campaignVendorSet = useMemo(() => {
    if (campaignFilter === "all") return null;
    const names = new Set<string>();
    for (const t of threads) {
      if (t.campaign_id === campaignFilter && t.vendor_name) {
        names.add(t.vendor_name);
      }
    }
    return names;
  }, [campaignFilter, threads]);

  // Vendor names with a non-terminal negotiation thread (active or needs_attention)
  const activeNegotiationVendors = useMemo(() => {
    const names: string[] = [];
    for (const t of threads) {
      if ((t.status === "active" || t.status === "needs_attention") && t.vendor_name) {
        names.push(t.vendor_name);
      }
    }
    return names;
  }, [threads]);

  // Apply filters
  const filtered = useMemo(() => {
    let result = roster;

    if (campaignVendorSet !== null) {
      result = result.filter((e) => campaignVendorSet.has(e.vendor_name));
    }

    if (search) {
      const q = search.toLowerCase();
      result = result.filter((e) => e.vendor_name.toLowerCase().includes(q));
    }

    if (selectedTerms.length > 0) {
      result = result.filter((e) => {
        const allTerms = e.terms_used.length > 0 ? e.terms_used : [e.payment_terms];
        return selectedTerms.some((t) => allTerms.includes(t));
      });
    }

    if (selectedSignals.length > 0) {
      result = result.filter((e) =>
        selectedSignals.every((sk) => e.signals.some((s) => s.key === sk))
      );
    }

    const minVal = parseFloat(minSpend);
    const maxVal = parseFloat(maxSpend);
    if (!isNaN(minVal)) {
      result = result.filter((e) => e.total_invoiced >= minVal);
    }
    if (!isNaN(maxVal)) {
      result = result.filter((e) => e.total_invoiced <= maxVal);
    }

    if (stageFilter === "excluded") {
      result = result.filter((e) => e.is_excluded);
    } else if (stageFilter !== "all") {
      result = result.filter((e) => e.stage === stageFilter);
    }

    if (labelFilter !== null) {
      result = result.filter((e) =>
        (labelsByVendor[e.vendor_name] ?? []).some((l) => l.id === labelFilter)
      );
    }

    return result;
  }, [
    roster,
    campaignVendorSet,
    search,
    selectedTerms,
    selectedSignals,
    minSpend,
    maxSpend,
    stageFilter,
    labelFilter,
    labelsByVendor,
  ]);

  // Apply sort
  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    return sortRoster(filtered, sortKey, sortDir);
  }, [filtered, sortKey, sortDir]);

  const paged = sorted.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  function handleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
    setPage(0);
  }

  function resetPage() {
    setPage(0);
  }

  function toggleSelect(name: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }

  function selectAll() {
    setSelected(new Set(filtered.map((e) => e.vendor_name)));
  }

  function clearAll() {
    setSelected(new Set());
  }

  function toggleTerm(term: string) {
    setSelectedTerms((prev) =>
      prev.includes(term) ? prev.filter((t) => t !== term) : [...prev, term]
    );
    resetPage();
  }

  function toggleSignal(key: SignalKey) {
    setSelectedSignals((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
    resetPage();
  }

  function handleLabelFilterChange(labelId: string | null) {
    setLabelFilter(labelId);
    resetPage();
  }

  // Campaign dialog: when the operator has a checkbox selection, those names become
  // preselected (already in the working list). Suggested quick-adds are the top-scored
  // vendors from the filtered set, excluding any already preselected.
  // When there is no selection, preselected is empty and suggestions fill the chips.
  const preselectedVendorNames = useMemo(() => {
    if (selected.size > 0) return Array.from(selected);
    return [];
  }, [selected]);

  const suggestedVendorNames = useMemo(() => {
    const preselectedSet = new Set(preselectedVendorNames);
    return filtered
      .slice()
      .sort((a, b) => b.score - a.score)
      .slice(0, 5)
      .map((e) => e.vendor_name)
      .filter((name) => !preselectedSet.has(name));
  }, [filtered, preselectedVendorNames]);

  if (roster.length === 0) {
    return (
      <div className="space-y-4">
        {headerActions && <div className="flex justify-end">{headerActions}</div>}
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <Building2 className="w-10 h-10 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">
            No vendors yet. Upload PAR invoice data to populate the vendor list.
          </p>
        </div>
      </div>
    );
  }

  const sortProps = {
    currentKey: sortKey,
    currentDir: sortDir,
    onSort: handleSort,
  };

  return (
    <div className="space-y-4">
      {/* ── Top bar: Campaign filter + Create campaign button ── */}
      <div className="flex items-center gap-3 flex-wrap">
        {/* Campaign dropdown */}
        <Select
          value={campaignFilter}
          onValueChange={(v) => {
            setCampaignFilter(v);
            resetPage();
          }}
        >
          <SelectTrigger className="w-[200px] h-8 text-xs">
            <SelectValue placeholder="All vendors" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" className="text-xs">
              All vendors
            </SelectItem>
            {campaigns.map((c) => (
              <SelectItem key={c.id} value={c.id} className="text-xs">
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Excluded-count affordance */}
        {excludedCount > 0 && stageFilter !== "excluded" && (
          <button
            onClick={() => {
              setStageFilter("excluded");
              resetPage();
            }}
            aria-label={`View ${excludedCount} excluded vendor${excludedCount > 1 ? "s" : ""}`}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive transition-colors"
          >
            <Ban className="w-3 h-3" />
            {excludedCount} excluded · view
          </button>
        )}

        <div className="ml-auto flex items-center gap-2">
          {selected.size > 0 && (
            <>
              <span className="text-xs text-muted-foreground">{selected.size} selected</span>
              <button
                onClick={clearAll}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                Clear
              </button>
            </>
          )}
          {/* Unified label control — filter when none selected, setter when vendors selected */}
          <UnifiedLabelControl
            labels={labels}
            selectedVendors={selected}
            labelFilter={labelFilter}
            labelsByVendor={labelsByVendor}
            onAssign={assignLabel}
            onUnassign={unassignLabel}
            onCreateLabel={createLabel}
            onRenameLabel={renameLabel}
            onDeleteLabel={deleteLabel}
            onLabelFilterChange={handleLabelFilterChange}
          />
          <Button size="sm" className="h-8 text-xs gap-1.5" onClick={() => setCampaignOpen(true)}>
            <MessageSquarePlus className="w-3.5 h-3.5" />
            {selected.size > 0
              ? `Create campaign (${selected.size})`
              : "Create negotiation campaign"}
          </Button>
          {headerActions}
        </div>
      </div>

      {/* ── Filters bar ── */}
      <div className="flex flex-wrap gap-2 items-start">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <Input
            placeholder="Search vendors…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              resetPage();
            }}
            className="pl-8 h-8 text-xs w-48"
          />
        </div>

        {/* Spend range */}
        <div className="flex items-center gap-1">
          <Input
            placeholder="Min spend"
            value={minSpend}
            onChange={(e) => {
              setMinSpend(e.target.value);
              resetPage();
            }}
            className="h-8 text-xs w-24"
            type="number"
            min={0}
          />
          <span className="text-xs text-muted-foreground">–</span>
          <Input
            placeholder="Max spend"
            value={maxSpend}
            onChange={(e) => {
              setMaxSpend(e.target.value);
              resetPage();
            }}
            className="h-8 text-xs w-24"
            type="number"
            min={0}
          />
        </div>

        {/* Stage / status filter (includes Excluded as a special option) */}
        <Select
          value={stageFilter}
          onValueChange={(v) => {
            setStageFilter(v as StageFilterValue);
            resetPage();
          }}
        >
          <SelectTrigger className="h-8 text-xs w-[140px]">
            <SelectValue placeholder="All stages" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" className="text-xs">
              All stages
            </SelectItem>
            {ALL_STAGES.map((s) => (
              <SelectItem key={s} value={s} className="text-xs">
                {s}
              </SelectItem>
            ))}
            <SelectItem value="excluded" className="text-xs text-destructive">
              Excluded
            </SelectItem>
          </SelectContent>
        </Select>

        {/* Spend window select */}
        <Select
          value={spendWindow}
          onValueChange={(v) => handleSpendWindowChange(v as SpendWindow)}
        >
          <SelectTrigger className="h-8 text-xs w-[170px]" aria-label="Spend time window">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(["3m", "6m", "12m", "all"] as SpendWindow[]).map((w) => (
              <SelectItem key={w} value={w} className="text-xs">
                {SPEND_WINDOW_LABELS[w]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Select-all / clear links */}
        <div className="ml-auto flex items-center gap-2 text-xs text-muted-foreground self-center">
          <button onClick={selectAll} className="hover:text-foreground transition-colors">
            Select all ({filtered.length})
          </button>
        </div>
      </div>

      {/* Active term filters */}
      {selectedTerms.length > 0 && (
        <div className="flex flex-wrap gap-1.5 items-center">
          <span className="text-xs text-muted-foreground whitespace-nowrap">Filtered by term:</span>
          {selectedTerms.map((term) => (
            <button
              key={term}
              onClick={() => toggleTerm(term)}
              aria-label={`Remove filter: ${term}`}
              className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border border-primary bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
            >
              {term}
              <X className="w-2.5 h-2.5" />
            </button>
          ))}
          <button
            onClick={() => {
              setSelectedTerms([]);
              resetPage();
            }}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors ml-1"
          >
            Clear all
          </button>
        </div>
      )}

      {/* Active signal filters */}
      {selectedSignals.length > 0 && (
        <div className="flex flex-wrap gap-1.5 items-center">
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            Filtered by signal:
          </span>
          {selectedSignals.map((key) => {
            const meta = SIGNAL_META[key];
            return (
              <button
                key={key}
                onClick={() => toggleSignal(key)}
                aria-label={`Remove signal filter: ${meta.tooltip.split("—")[0].trim()}`}
                className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border border-primary bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
              >
                <meta.Icon className="w-2.5 h-2.5" />
                {meta.tooltip.split("—")[0].trim()}
                <X className="w-2.5 h-2.5" />
              </button>
            );
          })}
          <button
            onClick={() => {
              setSelectedSignals([]);
              resetPage();
            }}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors ml-1"
          >
            Clear all
          </button>
        </div>
      )}

      {/* ── Table ── */}
      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              {/* Checkbox column */}
              <TableHead className="w-8">
                <input
                  type="checkbox"
                  aria-label="Select all vendors on this page"
                  checked={paged.length > 0 && paged.every((e) => selected.has(e.vendor_name))}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelected((prev) => {
                        const next = new Set(prev);
                        paged.forEach((entry) => next.add(entry.vendor_name));
                        return next;
                      });
                    } else {
                      setSelected((prev) => {
                        const next = new Set(prev);
                        paged.forEach((entry) => next.delete(entry.vendor_name));
                        return next;
                      });
                    }
                  }}
                  className="rounded border-border"
                />
              </TableHead>

              <SortableHeader label="Name" sortKey="vendor_name" {...sortProps} />
              <SortableHeader label="Payment terms" sortKey="payment_terms" {...sortProps} />
              <TableHead className="text-xs font-semibold">Signals</TableHead>
              <SortableHeader
                label={`Spend (${SPEND_WINDOW_LABELS[spendWindow]})`}
                sortKey="total_invoiced"
                {...sortProps}
                className="text-right"
              />
              <SortableHeader label="Score" sortKey="score" {...sortProps} className="text-right" />
              <SortableHeader label="Stage" sortKey="stage" {...sortProps} />
            </TableRow>
          </TableHeader>
          <TableBody>
            {paged.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-xs text-muted-foreground py-10">
                  No vendors match your filters
                </TableCell>
              </TableRow>
            ) : (
              paged.map((entry) => {
                const displayTerms =
                  entry.terms_used.length > 0
                    ? entry.terms_used
                    : entry.payment_terms
                      ? [entry.payment_terms]
                      : [];
                return (
                  <TableRow
                    key={entry.vendor_name}
                    data-selected={selected.has(entry.vendor_name) || undefined}
                    className="cursor-pointer hover:bg-muted/30 data-[selected]:bg-primary/5 transition-colors group/row"
                    onClick={() => {
                      setModalVendor(entry);
                      onVendorClick?.(entry.vendor_name);
                    }}
                  >
                    {/* Checkbox — stop propagation so click doesn't also open modal */}
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        aria-label={`Select ${entry.vendor_name}`}
                        checked={selected.has(entry.vendor_name)}
                        onChange={() => toggleSelect(entry.vendor_name)}
                        className="rounded border-border"
                      />
                    </TableCell>

                    {/* Name */}
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div
                          className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                            entry.is_excluded ? "bg-muted/50" : "bg-primary/10"
                          }`}
                        >
                          <Building2
                            className={`w-3.5 h-3.5 ${entry.is_excluded ? "text-muted-foreground" : "text-primary"}`}
                          />
                        </div>
                        <div className="flex flex-col gap-0.5 min-w-0">
                          {/* Row 1: vendor name + attention indicator */}
                          <div className="flex items-center gap-1.5">
                            <span
                              className={`text-xs font-semibold ${
                                entry.is_excluded
                                  ? "text-muted-foreground line-through"
                                  : "text-foreground"
                              }`}
                            >
                              {entry.vendor_name}
                            </span>
                            {entry.needs_attention && !entry.is_excluded && (
                              <TooltipProvider delayDuration={200}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <AlertCircle className="w-3 h-3 text-warning flex-shrink-0" />
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className="text-xs">
                                    Needs attention
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>

                          {/* Row 2: contact email/name + mode badge */}
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {entry.contact?.supplier_email ? (
                              <TooltipProvider delayDuration={200}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground leading-none truncate max-w-[180px]">
                                      <Mail className="w-2.5 h-2.5 flex-shrink-0" />
                                      {entry.contact.contact_name
                                        ? entry.contact.contact_name
                                        : entry.contact.supplier_email}
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className="text-xs max-w-[260px]">
                                    {entry.contact.contact_name && (
                                      <p className="font-medium">{entry.contact.contact_name}</p>
                                    )}
                                    <p>{entry.contact.supplier_email}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            ) : (
                              <span className="text-[10px] text-muted-foreground/60 italic leading-none">
                                No contact
                              </span>
                            )}

                            {/* Mode badge */}
                            {entry.mode === "auto" ? (
                              <span
                                aria-label="Mode: Auto"
                                className="inline-flex items-center gap-0.5 text-[10px] font-medium text-primary border border-primary/30 rounded px-1 py-0 bg-primary/5 leading-4"
                              >
                                <Zap className="w-2.5 h-2.5" />
                                Auto
                              </span>
                            ) : (
                              <span
                                aria-label="Mode: Draft-wait"
                                className="inline-flex items-center gap-0.5 text-[10px] font-medium text-muted-foreground border border-border/60 rounded px-1 py-0 bg-muted/40 leading-4"
                              >
                                Draft-wait
                              </span>
                            )}
                          </div>

                          {/* Row 3: label chips */}
                          {(labelsByVendor[entry.vendor_name] ?? []).length > 0 && (
                            <div
                              className="flex flex-wrap gap-0.5 mt-0.5"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {(labelsByVendor[entry.vendor_name] ?? []).map((label) => (
                                <LabelChip
                                  key={label.id}
                                  label={label}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setLabelFilter((prev) => (prev === label.id ? null : label.id));
                                    resetPage();
                                  }}
                                  onRemove={(e) => {
                                    e.stopPropagation();
                                    unassignLabel(entry.vendor_name, label.id);
                                  }}
                                />
                              ))}
                            </div>
                          )}

                          {/* Row 4a: campaign badges — click to filter to that campaign */}
                          {entry.campaignIds.length > 0 && (
                            <div
                              className="flex flex-wrap gap-0.5 mt-0.5"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {entry.campaignIds.map((cid, idx) => (
                                <button
                                  key={cid}
                                  onClick={() => {
                                    setCampaignFilter((prev) => (prev === cid ? "all" : cid));
                                    resetPage();
                                  }}
                                  aria-pressed={campaignFilter === cid}
                                  aria-label={`Campaign: ${entry.campaignNames[idx] ?? cid}`}
                                  className={`inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0 rounded border leading-4 transition-colors ${
                                    campaignFilter === cid
                                      ? "border-primary bg-primary/15 text-primary"
                                      : "border-primary/40 bg-primary/5 text-primary/80 hover:bg-primary/10 hover:border-primary/60"
                                  }`}
                                >
                                  <MessageSquarePlus className="w-2.5 h-2.5 flex-shrink-0" />
                                  {entry.campaignNames[idx] ?? cid}
                                </button>
                              ))}
                            </div>
                          )}

                          {/* Row 4b: excluded badge + re-include */}
                          {entry.is_excluded && (
                            <div className="flex items-center gap-1.5">
                              <TooltipProvider delayDuration={200}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-destructive border border-destructive/30 rounded px-1 py-0 bg-destructive/5 cursor-default">
                                      <Ban className="w-2.5 h-2.5" />
                                      Excluded
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className="text-xs max-w-[200px]">
                                    {entry.exclusion_reason
                                      ? entry.exclusion_reason
                                      : "Excluded from negotiations"}
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setExcluded(entry.vendor_name, false);
                                }}
                                aria-label={`Re-include ${entry.vendor_name}`}
                                title="Re-include in negotiations"
                                className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground hover:text-foreground transition-colors"
                              >
                                <RotateCcw className="w-2.5 h-2.5" />
                                Re-include
                              </button>
                            </div>
                          )}

                          {/* Row 4c: Exclude action — only shown on non-excluded rows */}
                          {!entry.is_excluded && (
                            <div onClick={(e) => e.stopPropagation()}>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setExcludeTarget(entry.vendor_name);
                                }}
                                aria-label={`Exclude ${entry.vendor_name}`}
                                className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground/60 hover:text-destructive transition-colors opacity-0 group-hover/row:opacity-100"
                              >
                                <Ban className="w-2.5 h-2.5" />
                                Exclude
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </TableCell>

                    {/* Payment terms — chips from terms_used */}
                    <TableCell>
                      {displayTerms.length > 0 ? (
                        <div className="flex flex-wrap gap-1" onClick={(e) => e.stopPropagation()}>
                          {displayTerms.map((term) => (
                            <button
                              key={term}
                              onClick={() => toggleTerm(term)}
                              aria-pressed={selectedTerms.includes(term)}
                              aria-label={`Filter by term: ${term}`}
                              className={`text-[10px] px-1.5 py-0 rounded border transition-colors leading-5 ${
                                selectedTerms.includes(term)
                                  ? "border-primary bg-primary/10 text-primary"
                                  : "border-border/60 text-muted-foreground hover:border-border hover:text-foreground"
                              }`}
                            >
                              {term}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>

                    {/* Signals — click to filter */}
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {entry.signals.length > 0 ? (
                        <TooltipProvider delayDuration={200}>
                          <div className="flex items-center gap-1.5">
                            {entry.signals.map((sig) => {
                              const meta = SIGNAL_META[sig.key];
                              const isActive = selectedSignals.includes(sig.key);
                              return (
                                <Tooltip key={sig.key}>
                                  <TooltipTrigger asChild>
                                    <button
                                      onClick={() => toggleSignal(sig.key)}
                                      aria-pressed={isActive}
                                      aria-label={sig.label}
                                      className={`inline-flex items-center justify-center w-5 h-5 rounded transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                                        isActive
                                          ? "ring-2 ring-primary bg-primary/10"
                                          : "hover:bg-muted/60"
                                      } ${meta.colorClass}`}
                                    >
                                      <meta.Icon className="w-3.5 h-3.5" />
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className="max-w-xs text-xs">
                                    <p className="font-medium">{sig.label}</p>
                                    <p className="text-muted-foreground">{meta.tooltip}</p>
                                    <p className="text-muted-foreground/70 mt-0.5">
                                      {isActive ? "Click to remove filter" : "Click to filter"}
                                    </p>
                                  </TooltipContent>
                                </Tooltip>
                              );
                            })}
                          </div>
                        </TooltipProvider>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>

                    {/* Spend */}
                    <TableCell className="text-right">
                      <span className="text-xs font-semibold">
                        {entry.total_invoiced > 0 ? formatCurrency(entry.total_invoiced) : "—"}
                      </span>
                    </TableCell>

                    {/* Score */}
                    <TableCell className="text-right">
                      {entry.score > 0 ? (
                        <Badge
                          variant="outline"
                          className="text-[10px] px-1.5 py-0 border-border/60"
                        >
                          {entry.score}
                        </Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>

                    {/* Stage */}
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={`text-[10px] px-1.5 py-0 border ${STAGE_BADGE[entry.stage]}`}
                      >
                        {entry.stage}
                      </Badge>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>

      <TablePagination
        page={page}
        pageSize={PAGE_SIZE}
        total={filtered.length}
        onPageChange={setPage}
      />

      {/* Campaign dialog */}
      <CampaignDialog
        open={campaignOpen}
        onOpenChange={setCampaignOpen}
        preselectedVendorNames={preselectedVendorNames}
        suggestedVendorNames={suggestedVendorNames}
        allVendorNames={allVendorNames}
        activeNegotiationVendors={activeNegotiationVendors}
      />

      {/* Vendor detail modal — renders the same content as the Vendor Detail page */}
      <Dialog
        open={modalVendor !== null}
        onOpenChange={(open) => {
          if (!open) setModalVendor(null);
        }}
      >
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-sm font-semibold">
              <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 bg-primary/10">
                <Building2 className="w-3.5 h-3.5 text-primary" />
              </div>
              {modalVendor?.vendor_name}
            </DialogTitle>
          </DialogHeader>
          {modalVendor && (
            <VendorDrillDown vendor={modalVendor.vendor_name} invoices={allInvoiceRows} />
          )}
        </DialogContent>
      </Dialog>

      {/* Exclude vendor dialog */}
      <ExcludeVendorDialog
        vendorName={excludeTarget}
        open={excludeTarget !== null}
        onOpenChange={(open) => {
          if (!open) setExcludeTarget(null);
        }}
      />
    </div>
  );
}
